import { useCallback, useEffect, useMemo, useState } from "react";

import { useLocation } from "react-router-dom";

import { getErrorMessage, useRequest } from "~/v2/api/hooks";
import { queryKeys } from "~/v2/api/queryKeys";
import type { Account } from "~/v2/api/rest/account";
import type {
  AuthxApplication,
  AuthxAuthorizationErrorCode,
  AuthxAuthorizeSubject,
} from "~/v2/api/rest/authx";
import {
  authorizeAuthxApplication,
  createAuthxErrorRedirect,
  getAuthxApplication,
} from "~/v2/api/rest/authx";
import { useAccountContext } from "~/v2/providers/AccountProvider";

const TRADING_API_GRANTS = ["trading:all:read", "trading:all:write"];
const MARKET_DATA_API_GRANTS = ["trading:data:read"];
const APP_HOST_PREFIX = "app.";

type BaseSupportedResource = {
  subjectType: AuthxAuthorizeSubject["subject_type"];
  label: string;
  accessLabel: string;
  grants: string[];
};

type ManualSupportedResource = BaseSupportedResource & {
  product: "live" | "paper";
  selectionMode: "manual";
};

type AutomaticSupportedResource = BaseSupportedResource & {
  product: "auto";
  selectionMode: "automatic";
};

type SupportedResource = ManualSupportedResource | AutomaticSupportedResource;

type AuthorizeParams = {
  application_id: string;
  client_id: string;
  code_challenge: string;
  code_challenge_method: string;
  redirect_uri: string;
  resource: string;
  state: string;
};

export type AuthxAuthorizationAccountOption = {
  id: string;
  label: string;
  name: string;
  isSelected: boolean;
};

const getUrlHostname = (url: string | undefined): string | undefined => {
  if (!url) {
    return undefined;
  }

  try {
    return new URL(url).hostname;
  } catch {
    return undefined;
  }
};

const getDerivedResourceHostname = (
  appHostname: string | undefined,
  resourceSubdomain: string,
): string | undefined => {
  if (!appHostname?.startsWith(APP_HOST_PREFIX)) {
    return undefined;
  }

  return `${resourceSubdomain}.${appHostname.slice(APP_HOST_PREFIX.length)}`;
};

const getMarketDataResourceHostnames = (): Set<string> =>
  new Set(
    [
      getUrlHostname(window.env?.DATA_API_URL),
      getDerivedResourceHostname(window.location.hostname, "data"),
      getDerivedResourceHostname(
        getUrlHostname(window.env?.ACATS_API_URL),
        "data",
      ),
    ].filter((hostname): hostname is string => !!hostname),
  );

const getAuthorizeParams = (search: string): AuthorizeParams => {
  const params = new URLSearchParams(search);

  return {
    application_id: params.get("application_id") ?? "",
    client_id: params.get("client_id") ?? "",
    code_challenge: params.get("code_challenge") ?? "",
    code_challenge_method: params.get("code_challenge_method") ?? "",
    redirect_uri: params.get("redirect_uri") ?? "",
    resource: params.get("resource") ?? "",
    state: params.get("state") ?? "",
  };
};

export const getSupportedResource = (
  resource: string,
): SupportedResource | undefined => {
  if (!resource) {
    return undefined;
  }

  try {
    const hostname = new URL(resource).hostname;

    if (hostname.startsWith("paper-api.")) {
      return {
        product: "paper",
        subjectType: "paccount",
        label: "Paper Trading API",
        accessLabel: "read/write",
        grants: TRADING_API_GRANTS,
        selectionMode: "manual",
      };
    }

    if (hostname.startsWith("api.")) {
      return {
        product: "live",
        subjectType: "account",
        label: "Live Trading API",
        accessLabel: "read/write",
        grants: TRADING_API_GRANTS,
        selectionMode: "manual",
      };
    }

    if (getMarketDataResourceHostnames().has(hostname)) {
      return {
        product: "auto",
        subjectType: "account",
        label: "Market Data API",
        accessLabel: "read-only",
        grants: MARKET_DATA_API_GRANTS,
        selectionMode: "automatic",
      };
    }

    return undefined;
  } catch {
    return undefined;
  }
};

const getAccountLabel = (account: Account, product: "live" | "paper") => {
  const accountType = product === "paper" ? "Paper" : "Live Account";

  return [
    accountType,
    account.account_number ? `(${account.account_number})` : undefined,
  ]
    .filter(Boolean)
    .join(" ");
};

const getAccountName = (account: Account) =>
  account.account_name || account.name;

export const getMarketDataAccount = (
  liveAccounts: Account[],
  paperAccounts: Account[],
) =>
  liveAccounts.find((account) => account.status === "ACTIVE") ??
  paperAccounts[0];

export const getAuthorizationSubject = ({
  liveAccounts,
  paperAccounts,
  resource,
  selectedAccountId,
}: {
  liveAccounts: Account[];
  paperAccounts: Account[];
  resource?: SupportedResource;
  selectedAccountId: string;
}): (AuthxAuthorizeSubject & { product: "live" | "paper" }) | undefined => {
  if (!resource) {
    return undefined;
  }

  if (resource.selectionMode === "automatic") {
    const liveAccount = liveAccounts.find(
      (account) => account.status === "ACTIVE",
    );
    if (liveAccount) {
      return {
        product: "live",
        subject_type: "account",
        subject_id: liveAccount.id,
        grants: resource.grants,
      };
    }

    const paperAccount = paperAccounts[0];
    if (!paperAccount) {
      return undefined;
    }

    return {
      product: "paper",
      subject_type: "paccount",
      subject_id: paperAccount.id,
      grants: resource.grants,
    };
  }

  if (!selectedAccountId) {
    return undefined;
  }

  return {
    product: resource.product,
    subject_type: resource.subjectType,
    subject_id: selectedAccountId,
    grants: resource.grants,
  };
};

const getClientUrl = (clientId: string) => {
  try {
    const url = new URL(clientId);

    if (!["http:", "https:"].includes(url.protocol)) {
      return undefined;
    }

    return url.toString();
  } catch {
    return undefined;
  }
};

const getAuthorizationErrorCode = (
  error: unknown,
): AuthxAuthorizationErrorCode => {
  const status = (error as { response?: { status?: number } }).response?.status;

  if (status === 503) {
    return "temporarily_unavailable";
  }

  if (status && status >= 500) {
    return "server_error";
  }

  return "invalid_request";
};

const getAuthxErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? getErrorMessage(error) : fallback;

export const useAuthxAuthorizationPageController = () => {
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");
  const { liveAccounts, paperAccounts, isLoading } = useAccountContext();
  const { search } = useLocation();

  const params = useMemo(() => getAuthorizeParams(search), [search]);
  const {
    data: authxApplication,
    error: authxApplicationError,
    isLoading: isApplicationLoading,
  } = useRequest<AuthxApplication>(
    queryKeys.authx.application(params.application_id),
    () => getAuthxApplication(params.application_id),
    {
      enabled: !!params.application_id,
      // Suppress the shared error toast for this query.
      meta: { showErrorToast: false },
      retry: false,
      staleTime: 5 * 60 * 1000,
    },
  );
  const resource = useMemo(
    () => getSupportedResource(params.resource),
    [params.resource],
  );
  const authorizationSubject = useMemo(
    () =>
      getAuthorizationSubject({
        liveAccounts,
        paperAccounts,
        resource,
        selectedAccountId,
      }),
    [liveAccounts, paperAccounts, resource, selectedAccountId],
  );
  const effectiveProduct =
    resource?.selectionMode === "automatic"
      ? authorizationSubject?.product
      : resource?.product;
  const displayProduct =
    effectiveProduct ??
    (resource?.selectionMode === "automatic" ? "live" : resource?.product);
  const accounts =
    resource?.selectionMode === "automatic"
      ? [getMarketDataAccount(liveAccounts, paperAccounts)].filter(
          (account): account is Account => !!account,
        )
      : resource?.product === "paper"
        ? paperAccounts
        : liveAccounts;

  const missingParams = [
    "application_id",
    "client_id",
    "code_challenge",
    "code_challenge_method",
    "redirect_uri",
    "resource",
  ].filter((key) => !params[key as keyof AuthorizeParams]);

  const accountOptions = useMemo<AuthxAuthorizationAccountOption[]>(() => {
    if (!resource) {
      return [];
    }

    return accounts.map((account) => ({
      id: account.id,
      label: getAccountLabel(
        account,
        resource.selectionMode === "automatic"
          ? (authorizationSubject?.product ?? "live")
          : resource.product,
      ),
      name: getAccountName(account),
      isSelected:
        resource.selectionMode === "automatic"
          ? account.id === authorizationSubject?.subject_id
          : account.id === selectedAccountId,
    }));
  }, [accounts, authorizationSubject, resource, selectedAccountId]);

  const redirectWithError = useCallback(
    async (error: AuthxAuthorizationErrorCode, errorDescription?: string) => {
      if (!params.application_id || !params.client_id || !params.redirect_uri) {
        setError(
          errorDescription ?? "Unable to redirect back to this application.",
        );
        setIsSubmitting(false);
        return;
      }

      try {
        const response = await createAuthxErrorRedirect(params.application_id, {
          client_id: params.client_id,
          redirect_uri: params.redirect_uri,
          error,
          ...(errorDescription ? { error_description: errorDescription } : {}),
          ...(params.state ? { state: params.state } : {}),
        });

        const redirectUrl = response.redirect_uri;
        if (!redirectUrl) {
          throw new Error(
            "The error redirect response did not include a redirect URL.",
          );
        }

        setRedirectUrl(redirectUrl);
      } catch (redirectError) {
        setError(
          getAuthxErrorMessage(
            redirectError,
            errorDescription ?? "Unable to redirect back to this application.",
          ),
        );
        setIsSubmitting(false);
      }
    },
    [
      params.application_id,
      params.client_id,
      params.redirect_uri,
      params.state,
    ],
  );

  useEffect(() => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  }, [redirectUrl]);

  const onDeny = async () => {
    setIsSubmitting(true);
    setError("");
    await redirectWithError("access_denied", "User denied consent.");
  };

  const onAllow = async () => {
    if (
      !resource ||
      !authorizationSubject ||
      !authxApplication ||
      isApplicationLoading ||
      missingParams.length > 0
    ) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await authorizeAuthxApplication({
        application_id: params.application_id,
        client_id: params.client_id,
        redirect_uri: params.redirect_uri,
        code_challenge: params.code_challenge,
        code_challenge_method: params.code_challenge_method,
        ...(params.state ? { state: params.state } : {}),
        subjects: [
          {
            subject_type: authorizationSubject.subject_type,
            subject_id: authorizationSubject.subject_id,
            grants: authorizationSubject.grants,
          },
        ],
      });

      const redirectUrl = response.redirect_uri;
      if (!redirectUrl) {
        throw new Error(
          "The authorization response did not include a redirect URL.",
        );
      }

      setRedirectUrl(redirectUrl);
    } catch (err) {
      await redirectWithError(
        getAuthorizationErrorCode(err),
        "Unable to authorize this application.",
      );
    }
  };

  const isInvalidRequest = missingParams.length > 0 || !resource;
  const errorMessage = error
    ? error
    : authxApplicationError
      ? "Unable to load this application."
      : !resource
        ? "This resource is not supported yet."
        : missingParams.length > 0
          ? `The authorization request is missing: ${missingParams.join(", ")}`
          : undefined;

  return {
    accountOptions: authxApplication ? accountOptions : [],
    accountTypeLabel: displayProduct === "paper" ? "Paper Account" : "Account",
    clientName: authxApplication?.name ?? "this application",
    clientUrl: authxApplication ? getClientUrl(params.client_id) : undefined,
    errorMessage,
    isAllowDisabled:
      isSubmitting ||
      isLoading ||
      isApplicationLoading ||
      !!authxApplicationError ||
      isInvalidRequest ||
      !authxApplication ||
      !authorizationSubject,
    isLoading,
    isSubmitting,
    onAllow,
    onDeny,
    onSelectAccount: setSelectedAccountId,
    product: authxApplication ? displayProduct : undefined,
    redirectUrl,
    requestedAccessLabel: authxApplication ? resource?.label : undefined,
    requestedAccessTypeLabel: authxApplication
      ? resource?.accessLabel
      : undefined,
  };
};
